import React, { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { CalendarPlus, Check, Download, FileSpreadsheet, FileText, Pencil, RotateCw, Save, Search, Trash2, Upload, X } from 'lucide-react';
import { useApiClient } from '../auth/useApiClient';

const emptyCourse = {
  title: '',
  programmeCode: '',
  discipline: '',
  cimCategory: '',
  description: '',
  duration: '',
  fees: '',
  format: '',
  location: '',
  instructor: '',
  targetAudience: '',
  objectives: '',
  keyTopics: '',
  outcomes: '',
  testimonials: '',
  averageAge: '',
  averageExperience: '',
  academicDirector: '',
  programmeDirector: '',
  topIndustries: '',
  topNationalities: '',
  participantSeniorities: '',
  keyDetailsWebsite: '',
  keyDetailsSourceNote: '',
  participantProfile: '',
  timetable: '',
  sessionOverview: '',
  facultyContentSummary: '',
  intendedImpact: '',
  prerequisites: '',
  sectorFocus: '',
  organisationalOutcomes: '',
  sourceUrl: '',
  certificationEligible: true,
  status: 'draft',
  sourceClassification: '',
  sourceNotes: '',
  reason: ''
};

const emptyDate = {
  id: null,
  startDate: '',
  status: 'open',
  applicationDeadline: '',
  location: '',
  format: '',
  feeOverride: '',
  sourceUrl: '',
  reason: ''
};

const emptySourceDocument = {
  title: '',
  sourceType: 'web',
  classification: 'approved-programme-source',
  sourceUrl: '',
  notes: ''
};

const sourceTypeClassifications = {
  web: 'approved-programme-source',
  pdf: 'approved-programme-source',
  docx: 'approved-programme-source',
  pptx: 'approved-programme-source',
  brochure: 'approved-programme-source',
  'case-study': 'approved-case-study',
  prework: 'approved-prework',
  timetable: 'approved-timetable'
};

const bulkSourceDocumentTypes = ['case-study', 'prework', 'timetable'];
const bulkSourceTemplateColumns = ['programmeCode', 'documentType', 'title', 'fileName', 'sourceUrl', 'notes'];
const bulkSourceTemplateRows = [
  {
    programmeCode: 'ADP',
    documentType: 'timetable',
    title: 'ADP timetable 2026',
    fileName: 'adp-timetable.pdf',
    sourceUrl: '',
    notes: 'Approved timetable for Query grounding'
  },
  {
    programmeCode: 'SEP',
    documentType: 'case-study',
    title: 'SEP board case study',
    fileName: 'sep-board-case.pdf',
    sourceUrl: '',
    notes: 'Approved case study pack'
  },
  {
    programmeCode: 'LCP',
    documentType: 'prework',
    title: 'LCP prework pack',
    fileName: 'lcp-prework.docx',
    sourceUrl: '',
    notes: 'Approved participant prework'
  }
];

const emptyFaq = {
  id: null,
  question: '',
  answer: '',
  sourceDocument: '',
  sectionTitle: '',
  sequenceNumber: ''
};

const courseStatuses = ['draft', 'published', 'inactive', 'archived'];
const dateStatuses = ['open', 'full', 'cancelled', 'tentative'];
const publishRequiredFields = new Set(['title', 'programmeCode', 'discipline', 'description', 'duration', 'format', 'sourceUrl']);
const CATALOGUE_UPDATED_EVENT = 'execed:catalogue-updated';
const CATALOGUE_UPDATED_STORAGE_KEY = 'execed.catalogue.updatedAt';
const bulkTemplateColumns = [
  'action',
  'programmeCode',
  'title',
  'discipline',
  'programmePillar',
  'fees',
  'status',
  'testimonials',
  'averageAge',
  'averageExperience',
  'academicDirector',
  'programmeDirector',
  'topIndustries',
  'topNationalities',
  'participantSeniorities',
  'keyDetailsWebsite',
  'keyDetailsSourceNote',
  'participantProfile',
  'timetable',
  'sessionOverview',
  'facultyContentSummary',
  'intendedImpact',
  'prerequisites',
  'sectorFocus',
  'organisationalOutcomes',
  'startDate',
  'dateStatus',
  'applicationDeadline',
  'location',
  'format',
  'feeOverride',
  'faqQuestion',
  'faqAnswer',
  'faqSourceDocument',
  'faqSectionTitle',
  'faqSequenceNumber',
  'sourceUrl',
  'reason'
];
const bulkTemplateRows = [
  {
    action: 'update',
    programmeCode: 'ADP',
    title: 'Accelerated Development Programme',
    discipline: 'General Management',
    programmePillar: 'Leadership',
    fees: '9850',
    status: 'published',
    startDate: '2026-09-14',
    dateStatus: 'open',
    applicationDeadline: '2026-08-14',
    location: 'London',
    format: 'In person',
    feeOverride: '',
    faqQuestion: 'What is the application deadline?',
    faqAnswer: 'Applications should be submitted by the listed application deadline for the relevant start date.',
    faqSourceDocument: 'Programme FAQ update',
    faqSectionTitle: 'Applications',
    faqSequenceNumber: '1',
    sourceUrl: 'https://www.london.edu/executive-education',
    reason: 'Annual fee and start date update'
  },
  {
    action: 'update',
    programmeCode: '',
    title: 'New Programme Draft',
    discipline: 'Leadership',
    programmePillar: 'Leadership',
    fees: '7500',
    status: 'draft',
    participantProfile: 'Senior managers preparing for broader leadership responsibility.',
    intendedImpact: 'Build confidence, alignment, and practical leadership capability.',
    startDate: '2026-10-05',
    dateStatus: 'tentative',
    location: 'London',
    format: 'In person',
    faqQuestion: 'Who is this programme for?',
    faqAnswer: 'Senior managers preparing for broader leadership responsibility.',
    faqSourceDocument: 'New programme source',
    faqSectionTitle: 'Participant profile',
    faqSequenceNumber: '1',
    sourceUrl: 'https://www.london.edu/executive-education/new-programme',
    reason: 'New programme setup from approved source'
  }
];

function notifyCatalogueUpdated() {
  const updatedAt = String(Date.now());
  window.dispatchEvent(new CustomEvent(CATALOGUE_UPDATED_EVENT, { detail: { updatedAt } }));
  try {
    window.localStorage.setItem(CATALOGUE_UPDATED_STORAGE_KEY, updatedAt);
  } catch (error) {
    // Ignore storage failures; the in-page event is enough for the current tab.
  }
}

function RequiredFieldLabel({ field, children }) {
  return (
    <span className="catalogue-admin-field-label-text">
      {children}
      {publishRequiredFields.has(field) && (
        <span className="catalogue-admin-required-marker" aria-label="required to publish" title="Required to publish">*</span>
      )}
    </span>
  );
}

function escapeCsvCell(value) {
  const text = String(value ?? '');
  if (!/[",\r\n]/.test(text)) return text;
  return `"${text.replace(/"/g, '""')}"`;
}

function buildCsvTemplate() {
  const rows = [
    bulkTemplateColumns,
    ...bulkTemplateRows.map((row) => bulkTemplateColumns.map((column) => row[column] ?? ''))
  ];
  return rows.map((row) => row.map(escapeCsvCell).join(',')).join('\r\n');
}

function buildSourceCsvTemplate() {
  const rows = [
    bulkSourceTemplateColumns,
    ...bulkSourceTemplateRows.map((row) => bulkSourceTemplateColumns.map((column) => row[column] ?? ''))
  ];
  return rows.map((row) => row.map(escapeCsvCell).join(',')).join('\r\n');
}

function downloadCsv(filename, csv) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function stringifyTopics(value) {
  if (Array.isArray(value)) return value.join('\n');
  return value || '';
}

function stringifyTestimonials(value) {
  if (!Array.isArray(value)) return value || '';
  return value
    .map((item) => {
      if (typeof item === 'string') return item;
      const quote = item?.quote || item?.text || '';
      const attribution = [item?.name, item?.designation].filter(Boolean).join(', ');
      return [quote, attribution].filter(Boolean).join(' - ');
    })
    .filter(Boolean)
    .join('\n');
}

function stringifyList(value) {
  if (Array.isArray(value)) return value.join('\n');
  return value || '';
}

function mapCourseToDraft(course = {}) {
  const keyDetails = course.keyDetails || {};
  const contentDetails = course.contentDetails || {};
  return {
    ...emptyCourse,
    ...course,
    fees: course.fees ?? '',
    keyTopics: stringifyTopics(course.keyTopics),
    testimonials: stringifyTestimonials(course.testimonials),
    averageAge: keyDetails.averageAge || '',
    averageExperience: keyDetails.averageExperience || '',
    academicDirector: keyDetails.academicDirector || '',
    programmeDirector: keyDetails.programmeDirector || '',
    topIndustries: stringifyList(keyDetails.industries),
    topNationalities: stringifyList(keyDetails.nationalities),
    participantSeniorities: stringifyList(keyDetails.seniorities),
    keyDetailsWebsite: keyDetails.website || '',
    keyDetailsSourceNote: keyDetails.sourceNote || '',
    participantProfile: contentDetails.participantProfile || '',
    timetable: contentDetails.timetable || '',
    sessionOverview: contentDetails.sessionOverview || '',
    facultyContentSummary: contentDetails.facultyContentSummary || '',
    intendedImpact: contentDetails.intendedImpact || '',
    prerequisites: contentDetails.prerequisites || '',
    sectorFocus: contentDetails.sectorFocus || '',
    organisationalOutcomes: contentDetails.organisationalOutcomes || '',
    certificationEligible: course.certificationEligible !== false,
    reason: ''
  };
}

function mapDraftToPayload(draft) {
  const splitList = (value) => String(value || '')
    .split(/\r?\n|;/)
    .map((item) => item.trim())
    .filter(Boolean);
  return {
    ...draft,
    fees: draft.fees === '' ? null : Number.parseInt(String(draft.fees).replace(/[^0-9-]/g, ''), 10),
    keyTopics: splitList(draft.keyTopics),
    testimonials: String(draft.testimonials || '')
      .split(/\r?\n/)
      .map((item) => item.trim())
      .filter(Boolean)
      .map((quote) => ({ quote })),
    keyDetails: {
      averageAge: draft.averageAge || '',
      averageExperience: draft.averageExperience || '',
      academicDirector: draft.academicDirector || '',
      programmeDirector: draft.programmeDirector || '',
      industries: splitList(draft.topIndustries),
      nationalities: splitList(draft.topNationalities),
      seniorities: splitList(draft.participantSeniorities),
      website: draft.keyDetailsWebsite || '',
      sourceNote: draft.keyDetailsSourceNote || ''
    },
    contentDetails: {
      participantProfile: draft.participantProfile || '',
      timetable: draft.timetable || '',
      sessionOverview: draft.sessionOverview || '',
      facultyContentSummary: draft.facultyContentSummary || '',
      intendedImpact: draft.intendedImpact || '',
      prerequisites: draft.prerequisites || '',
      sectorFocus: draft.sectorFocus || '',
      organisationalOutcomes: draft.organisationalOutcomes || ''
    }
  };
}

function validateDraft(draft, publishReady = draft.status === 'published') {
  const required = publishReady
    ? ['title', 'programmeCode', 'discipline', 'description', 'duration', 'format', 'sourceUrl']
    : ['title'];
  return required
    .filter((field) => !String(draft[field] || '').trim())
    .map((field) => `${field} is required${publishReady ? ' before publishing' : ''}.`);
}

function cleanExtractedFields(fields = {}) {
  const pruneObject = (value) => Object.fromEntries(
    Object.entries(value || {}).filter(([, entry]) => {
      if (Array.isArray(entry)) return entry.length > 0;
      if (entry && typeof entry === 'object') return Object.keys(pruneObject(entry)).length > 0;
      return String(entry ?? '').trim() !== '';
    }).map(([key, entry]) => [key, entry && typeof entry === 'object' && !Array.isArray(entry) ? pruneObject(entry) : entry])
  );
  return pruneObject(fields);
}

function hasMeaningfulValue(value) {
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'boolean') return true;
  return String(value ?? '').trim() !== '';
}

function buildDraftFromExtraction(fields = {}, document = {}) {
  const extractedDraft = mapCourseToDraft({ ...fields, status: 'draft' });
  return (current) => {
    const next = { ...current };
    Object.keys(emptyCourse).forEach((field) => {
      if (field === 'id') return;
      const value = extractedDraft[field];
      if (hasMeaningfulValue(value)) {
        next[field] = value;
      }
    });
    if (document.sourceUrl && !next.sourceUrl) {
      next.sourceUrl = document.sourceUrl;
    }
    next.sourceClassification = document.classification || next.sourceClassification || 'approved-programme-source';
    const sourceNote = `Populated from brochure source ${document.id || ''}: ${document.title || document.filename || 'source document'}`.trim();
    next.sourceNotes = String(next.sourceNotes || '').includes(sourceNote)
      ? next.sourceNotes
      : [next.sourceNotes, sourceNote].filter(Boolean).join('\n');
    next.reason = next.reason || 'Created from brochure extraction';
    next.status = 'draft';
    return next;
  };
}

function formatFee(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return 'Not available';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(amount);
}

function parseDelimited(text) {
  const rows = [];
  const lines = text.split(/\r?\n/).filter((line) => line.trim());
  if (!lines.length) return rows;
  const delimiter = lines[0].includes('\t') ? '\t' : ',';
  const parseLine = (line) => {
    const cells = [];
    let current = '';
    let inQuotes = false;
    for (let index = 0; index < line.length; index += 1) {
      const char = line[index];
      const next = line[index + 1];
      if (char === '"' && next === '"') {
        current += '"';
        index += 1;
      } else if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === delimiter && !inQuotes) {
        cells.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    cells.push(current.trim());
    return cells;
  };
  const headers = parseLine(lines[0]).map((item) => item.trim());
  lines.slice(1).forEach((line) => {
    const cells = parseLine(line);
    const row = {};
    headers.forEach((header, index) => {
      row[header] = cells[index] || '';
    });
    rows.push(row);
  });
  return rows;
}

function getRowValue(row, ...keys) {
  for (const key of keys) {
    if (row[key] !== undefined && row[key] !== null && String(row[key]).trim() !== '') return row[key];
    const matchedKey = Object.keys(row).find((candidate) => candidate.toLowerCase().replace(/[^a-z0-9]/g, '') === key.toLowerCase().replace(/[^a-z0-9]/g, ''));
    if (matchedKey && row[matchedKey] !== undefined && row[matchedKey] !== null && String(row[matchedKey]).trim() !== '') return row[matchedKey];
  }
  return '';
}

function normalizeBulkSourceType(value = '') {
  const normalized = String(value || '').trim().toLowerCase().replace(/[_\s]+/g, '-');
  if (['case', 'case-study', 'case-studies'].includes(normalized)) return 'case-study';
  if (['prework', 'pre-work', 'pre'].includes(normalized)) return 'prework';
  if (['timetable', 'schedule', 'agenda'].includes(normalized)) return 'timetable';
  return normalized;
}

function sourceFileKey(value = '') {
  return String(value || '').trim().split(/[\\/]/).pop().toLowerCase();
}

function normalizeBulkSourceRows(rows = []) {
  return (Array.isArray(rows) ? rows : [])
    .map((row, index) => ({
      rowNumber: index + 1,
      programmeCode: String(getRowValue(row, 'programmeCode', 'programme code', 'code')).trim(),
      documentType: normalizeBulkSourceType(getRowValue(row, 'documentType', 'document type', 'type', 'sourceType', 'source type')),
      title: String(getRowValue(row, 'title', 'documentTitle', 'document title')).trim(),
      fileName: String(getRowValue(row, 'fileName', 'file name', 'filename')).trim(),
      sourceUrl: String(getRowValue(row, 'sourceUrl', 'source url', 'url')).trim(),
      notes: String(getRowValue(row, 'notes', 'review notes', 'reason')).trim()
    }))
    .filter((row) => row.programmeCode || row.documentType || row.title || row.fileName || row.sourceUrl);
}

async function parseBulkFile(file) {
  const extension = file.name.split('.').pop().toLowerCase();
  if (extension === 'xlsx' || extension === 'xls') {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: 'array' });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    return XLSX.utils.sheet_to_json(sheet, { defval: '' });
  }
  const text = await file.text();
  return parseDelimited(text);
}

function normalizeFaqUploadRows(rows = []) {
  return (Array.isArray(rows) ? rows : [])
    .map((row, index) => ({
      rowNumber: index + 1,
      question: String(getRowValue(row, 'question', 'faqQuestion', 'faq question')).trim(),
      answer: String(getRowValue(row, 'answer', 'faqAnswer', 'faq answer')).trim(),
      sourceDocument: String(getRowValue(row, 'sourceDocument', 'source document', 'source')).trim(),
      sectionTitle: String(getRowValue(row, 'sectionTitle', 'section title', 'section')).trim(),
      sequenceNumber: String(getRowValue(row, 'sequenceNumber', 'sequence number', 'order')).trim()
    }))
    .filter((row) => row.question || row.answer);
}

function CoursePreview({ draft }) {
  const topics = String(draft.keyTopics || '').split(/\r?\n|;/).map((item) => item.trim()).filter(Boolean);
  const testimonials = String(draft.testimonials || '').split(/\r?\n/).map((item) => item.trim()).filter(Boolean);
  const industries = String(draft.topIndustries || '').split(/\r?\n|;/).map((item) => item.trim()).filter(Boolean);
  return (
    <div className="catalogue-admin-preview-grid">
      <div className="catalogue-admin-preview-card">
        <div className="section-label">Catalogue Preview</div>
        <h3>{draft.title || 'Untitled programme'}</h3>
        <p>{draft.description || 'Description will appear here.'}</p>
        <dl>
          <div><dt>Code</dt><dd>{draft.programmeCode || 'Not set'}</dd></div>
          <div><dt>Fee</dt><dd>{formatFee(draft.fees)}</dd></div>
          <div><dt>Format</dt><dd>{draft.format || 'Not set'}</dd></div>
          <div><dt>Status</dt><dd>{draft.status}</dd></div>
        </dl>
      </div>
      <div className="catalogue-admin-preview-card">
        <div className="section-label">Query Preview</div>
        <p>
          {draft.programmeCode ? `[${draft.programmeCode}] ` : ''}
          {draft.title || 'This programme'} is a {draft.discipline || 'discipline not set'} programme for {draft.targetAudience || 'a target audience to be defined'}.
        </p>
        <p>{draft.objectives || 'Objectives will be used to ground Query responses.'}</p>
      </div>
      <div className="catalogue-admin-preview-card">
        <div className="section-label">Pathways Preview</div>
        <p>{draft.certificationEligible ? 'Counts toward CIM/CIF certificate recommendations.' : 'Does not count toward CIM/CIF certificate recommendations.'}</p>
        <p>{draft.cimCategory ? `Programme pillar/category: ${draft.cimCategory}` : 'No programme pillar/category set.'}</p>
        {topics.length > 0 && <p>Learning tags: {topics.slice(0, 5).join(', ')}</p>}
        {(draft.averageAge || draft.averageExperience) && <p>Participant profile: {[draft.averageAge, draft.averageExperience].filter(Boolean).join(' / ')}</p>}
        {industries.length > 0 && <p>Top industries: {industries.slice(0, 5).join(', ')}</p>}
        {draft.prerequisites && <p>Prerequisites: {draft.prerequisites}</p>}
        {draft.intendedImpact && <p>Impact: {draft.intendedImpact}</p>}
        {testimonials.length > 0 && <p>Participant evidence: {testimonials.slice(0, 2).join(' / ')}</p>}
      </div>
    </div>
  );
}

function CatalogueAdminPage({ embedded = false }) {
  const { request } = useApiClient();
  const [courses, setCourses] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [draft, setDraft] = useState(emptyCourse);
  const [dateDraft, setDateDraft] = useState(emptyDate);
  const [bulkRows, setBulkRows] = useState([]);
  const [bulkPreview, setBulkPreview] = useState([]);
  const [bulkSourceRows, setBulkSourceRows] = useState([]);
  const [bulkSourceFiles, setBulkSourceFiles] = useState([]);
  const [bulkSourcePreview, setBulkSourcePreview] = useState([]);
  const [baseBackfillResult, setBaseBackfillResult] = useState(null);
  const [sourceDocuments, setSourceDocuments] = useState([]);
  const [fieldSources, setFieldSources] = useState([]);
  const [sourceDraft, setSourceDraft] = useState(emptySourceDocument);
  const [sourceFile, setSourceFile] = useState(null);
  const [extractionPreview, setExtractionPreview] = useState(null);
  const [courseFaqs, setCourseFaqs] = useState([]);
  const [faqDraft, setFaqDraft] = useState(emptyFaq);
  const [faqUploadPreview, setFaqUploadPreview] = useState([]);
  const [faqUploadFile, setFaqUploadFile] = useState(null);
  const [newCourseSourceFile, setNewCourseSourceFile] = useState(null);
  const [newCourseSourcePreview, setNewCourseSourcePreview] = useState(null);
  const [query, setQuery] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [activeAdminTab, setActiveAdminTab] = useState('update');

  const selectedCourse = useMemo(
    () => courses.find((course) => course.id === selectedCourseId) || null,
    [courses, selectedCourseId]
  );

  const filteredCourses = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return courses;
    return courses.filter((course) => [
      course.title,
      course.programmeCode,
      course.discipline,
      course.cimCategory,
      course.status
    ].filter(Boolean).join(' ').toLowerCase().includes(normalized));
  }, [courses, query]);
  const hasCourseSearchQuery = query.trim().length > 0;

  const validationErrors = validateDraft(draft);
  const canEditExistingCourse = Boolean(selectedCourseId);
  const readOnlyFieldProps = { disabled: !isEditing };

  const loadCourses = async (nextSelectedId = selectedCourseId) => {
    const response = await request('/api/admin/courses');
    if (!response.ok) throw new Error('Could not load catalogue.');
    const data = await response.json();
    const nextCourses = Array.isArray(data.courses) ? data.courses : [];
    setCourses(nextCourses);
    const nextSelected = nextCourses.find((course) => course.id === nextSelectedId) || nextCourses[0] || null;
    if (nextSelected) {
      setSelectedCourseId(nextSelected.id);
      setDraft(mapCourseToDraft(nextSelected));
    }
  };

  const loadSourceDocuments = async (courseId = selectedCourseId) => {
    if (!courseId) {
      setSourceDocuments([]);
      setFieldSources([]);
      return;
    }
    const [documentsResponse, sourcesResponse] = await Promise.all([
      request(`/api/admin/source-documents?courseId=${courseId}`),
      request(`/api/admin/courses/${courseId}/field-sources`)
    ]);
    if (documentsResponse.ok) {
      const data = await documentsResponse.json();
      setSourceDocuments(Array.isArray(data.documents) ? data.documents : []);
    }
    if (sourcesResponse.ok) {
      const data = await sourcesResponse.json();
      setFieldSources(Array.isArray(data.sources) ? data.sources : []);
    }
  };

  const loadCourseFaqs = async (courseId = selectedCourseId) => {
    if (!courseId) {
      setCourseFaqs([]);
      return;
    }
    const response = await request(`/api/admin/courses/${courseId}/faqs`);
    if (response.ok) {
      const data = await response.json();
      setCourseFaqs(Array.isArray(data.faqs) ? data.faqs : []);
    }
  };

  useEffect(() => {
    setLoading(true);
    loadCourses()
      .catch((error) => setErrorMessage(error.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedCourse) {
      setDraft(mapCourseToDraft(selectedCourse));
      setDateDraft(emptyDate);
      setExtractionPreview(null);
      setIsEditing(false);
      loadSourceDocuments(selectedCourse.id);
      loadCourseFaqs(selectedCourse.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCourseId]);

  const updateDraft = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
  };

  const selectCourseForUpdate = (courseId) => {
    setSelectedCourseId(courseId);
    setQuery('');
  };

  const extractNewCourseBrochure = async (file = newCourseSourceFile) => {
    if (!file) throw new Error('Choose a brochure file before extracting course data.');
    const formData = new FormData();
    formData.append('title', file.name || 'Programme brochure');
    formData.append('sourceType', 'brochure');
    formData.append('classification', 'approved-programme-source');
    formData.append('sourceUrl', '');
    formData.append('notes', 'Uploaded while creating a new course draft.');
    formData.append('status', 'draft');
    formData.append('file', file);

    const uploadResponse = await request('/api/admin/source-documents', {
      method: 'POST',
      body: formData
    });
    const uploadData = await uploadResponse.json();
    if (!uploadResponse.ok) throw new Error(uploadData.message || 'Could not upload brochure.');

    const extractResponse = await request(`/api/admin/source-documents/${uploadData.document.id}/extract`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    });
    const extractData = await extractResponse.json();
    if (!extractResponse.ok) throw new Error(extractData.message || 'Could not extract brochure.');

    const preview = { document: extractData.document, extraction: extractData.extraction };
    setNewCourseSourcePreview(preview);
    setDraft(buildDraftFromExtraction(cleanExtractedFields(extractData.extraction?.fields || {}), extractData.document));
    const warnings = Array.isArray(extractData.extraction?.warnings) ? extractData.extraction.warnings.filter(Boolean) : [];
    setStatusMessage(warnings.length ? `Brochure extracted with review notes: ${warnings.join(' ')}` : 'Brochure extracted into the draft course.');
  };

  const handleNewCourseBrochureFile = async (event) => {
    const file = event.target.files?.[0] || null;
    setNewCourseSourceFile(file);
    setNewCourseSourcePreview(null);
    if (file) {
      await extractNewCourseBrochure(file);
    }
  };

  const linkNewCourseSource = async (courseId, sourcePreview = newCourseSourcePreview) => {
    if (!sourcePreview?.document?.id) return null;
    const extraction = sourcePreview.extraction || {};
    const fields = cleanExtractedFields(extraction.fields || {});
    const response = await request(`/api/admin/source-documents/${sourcePreview.document.id}/link-course`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        courseId,
        extractionPayload: extraction,
        fields,
        fieldSources: extraction.fieldSources || [],
        status: 'approved',
        notes: `Linked during new course creation: ${sourcePreview.document.title || sourcePreview.document.filename || 'brochure'}`
      })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Could not link brochure source to course.');
    return data;
  };

  const applyNewCourseFaqs = async (courseId, faqs = []) => {
    const rows = (Array.isArray(faqs) ? faqs : [])
      .filter((faq) => String(faq.question || '').trim() && String(faq.answer || '').trim())
      .map((faq, index) => ({
        question: faq.question,
        answer: faq.answer,
        sourceDocument: faq.sourceDocument || '',
        sectionTitle: faq.sectionTitle || '',
        sequenceNumber: faq.sequenceNumber || index + 1
      }));
    if (!rows.length) return null;
    const response = await request(`/api/admin/courses/${courseId}/faqs/bulk`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rows })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Could not save course FAQs.');
    return data;
  };

  const saveDraft = async ({ publish = false } = {}) => {
    const nextStatus = publish ? 'published' : undefined;
    const errors = validateDraft({ ...draft, status: nextStatus || draft.status }, publish);
    if (errors.length) {
      setErrorMessage(errors.join(' '));
      return;
    }
    setStatusMessage('');
    setErrorMessage('');
    const isNew = !selectedCourseId || draft.id === undefined || draft.id === null;
    const pendingNewCourseSource = isNew ? newCourseSourcePreview : null;
    const pendingNewCourseFaqs = isNew ? courseFaqs : [];
    const pendingNewCourseFaqFile = isNew ? faqUploadFile : null;
    const payload = mapDraftToPayload({
      ...draft,
      status: nextStatus || (isNew ? 'draft' : draft.status),
      reason: draft.reason || (publish ? 'Create and publish course' : draft.reason)
    });
    const response = await request(isNew ? '/api/admin/courses' : `/api/admin/courses/${selectedCourseId}`, {
      method: isNew ? 'POST' : 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Could not save course.');
    }
    const postCreateWarnings = [];
    if (pendingNewCourseSource?.document?.id) {
      try {
        await linkNewCourseSource(data.course.id, pendingNewCourseSource);
        setNewCourseSourceFile(null);
        setNewCourseSourcePreview(null);
      } catch (error) {
        postCreateWarnings.push(`Brochure source was not linked: ${error.message}`);
      }
    }
    if (pendingNewCourseFaqs.length) {
      try {
        await applyNewCourseFaqs(data.course.id, pendingNewCourseFaqs);
      } catch (error) {
        postCreateWarnings.push(`FAQs were not saved: ${error.message}`);
      }
    }
    if (pendingNewCourseFaqFile) {
      try {
        await uploadFaqSourceDocument(data.course.id, pendingNewCourseFaqFile, pendingNewCourseFaqs.length);
      } catch (error) {
        postCreateWarnings.push(`FAQ source file was not retained: ${error.message}`);
      }
    }
    setFaqDraft(emptyFaq);
    setFaqUploadPreview([]);
    setFaqUploadFile(null);
    setStatusMessage(publish
      ? 'Course published.'
      : (isNew
        ? (pendingNewCourseSource?.document?.id ? 'Draft course created from brochure.' : 'Draft course created.')
        : 'Course saved.'));
    if (postCreateWarnings.length) setErrorMessage(postCreateWarnings.join(' '));
    setIsEditing(false);
    notifyCatalogueUpdated();
    await loadCourses(data.course.id);
    if (isNew) setActiveAdminTab('update');
  };

  const publishDraft = async () => {
    if (!selectedCourseId) {
      await saveDraft({ publish: true });
      return;
    }
    const errors = validateDraft({ ...draft, status: 'published' }, true);
    if (errors.length) {
      setErrorMessage(errors.join(' '));
      return;
    }
    updateDraft('status', 'published');
    const response = await request(`/api/admin/courses/${selectedCourseId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(mapDraftToPayload({ ...draft, status: 'published', reason: draft.reason || 'Publish course' }))
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Could not publish course.');
    setStatusMessage('Course published.');
    setIsEditing(false);
    notifyCatalogueUpdated();
    await loadCourses(data.course.id);
  };

  const createNewDraft = () => {
    setActiveAdminTab('add');
    setSelectedCourseId(null);
    setDraft(emptyCourse);
    setDateDraft(emptyDate);
    setSourceDocuments([]);
    setFieldSources([]);
    setCourseFaqs([]);
    setFaqDraft(emptyFaq);
    setFaqUploadPreview([]);
    setExtractionPreview(null);
    setNewCourseSourceFile(null);
    setNewCourseSourcePreview(null);
    setIsEditing(true);
    setStatusMessage('');
    setErrorMessage('');
  };

  const openUpdateTab = () => {
    setActiveAdminTab('update');
    setStatusMessage('');
    setErrorMessage('');
    setIsEditing(false);
    setDateDraft(emptyDate);
    setExtractionPreview(null);
    setNewCourseSourcePreview(null);
    setFaqDraft(emptyFaq);
    setFaqUploadPreview([]);
    const nextCourse = selectedCourse || courses[0] || null;
    if (nextCourse) {
      setSelectedCourseId(nextCourse.id);
      setDraft(mapCourseToDraft(nextCourse));
    }
  };

  const openBulkTab = () => {
    setActiveAdminTab('bulk');
    setStatusMessage('');
    setErrorMessage('');
    setIsEditing(false);
    setNewCourseSourcePreview(null);
    setFaqDraft(emptyFaq);
    setFaqUploadPreview([]);
  };

  const startEditing = () => {
    if (!selectedCourseId) return;
    setStatusMessage('Editing enabled. Save or publish when ready.');
    setErrorMessage('');
    setIsEditing(true);
  };

  const cancelEditing = () => {
    if (selectedCourse) {
      setDraft(mapCourseToDraft(selectedCourse));
      setDateDraft(emptyDate);
      setExtractionPreview(null);
      setFaqDraft(emptyFaq);
      setFaqUploadPreview([]);
      setStatusMessage('Changes discarded.');
    } else {
      const fallbackCourse = courses[0] || null;
      if (fallbackCourse) {
        setActiveAdminTab('update');
        setSelectedCourseId(fallbackCourse.id);
        setDraft(mapCourseToDraft(fallbackCourse));
      } else {
        setDraft(emptyCourse);
      }
      setStatusMessage('');
    }
    setErrorMessage('');
    setIsEditing(false);
  };

  const saveDate = async () => {
    if (!selectedCourseId) return;
    const method = dateDraft.id ? 'PUT' : 'POST';
    const url = dateDraft.id
      ? `/api/admin/courses/${selectedCourseId}/dates/${dateDraft.id}`
      : `/api/admin/courses/${selectedCourseId}/dates`;
    const response = await request(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(dateDraft)
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Could not save date.');
    setDateDraft(emptyDate);
    setStatusMessage('Date saved.');
    notifyCatalogueUpdated();
    await loadCourses(selectedCourseId);
  };

  const editDate = (record) => {
    setDateDraft({
      id: record.id,
      startDate: record.startDate || '',
      status: record.status || 'open',
      applicationDeadline: record.applicationDeadline || '',
      location: record.location || '',
      format: record.format || '',
      feeOverride: record.feeOverride ?? '',
      sourceUrl: record.sourceUrl || '',
      reason: ''
    });
  };

  const cancelDate = async (record) => {
    setDateDraft({ ...emptyDate, ...record, status: 'cancelled', reason: 'Marked cancelled by admin' });
    const response = await request(`/api/admin/courses/${selectedCourseId}/dates/${record.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...record, status: 'cancelled', reason: 'Marked cancelled by admin' })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Could not cancel date.');
    setStatusMessage('Date marked cancelled.');
    notifyCatalogueUpdated();
    await loadCourses(selectedCourseId);
  };

  const removeDate = async (record) => {
    const response = await request(`/api/admin/courses/${selectedCourseId}/dates/${record.id}?reason=${encodeURIComponent('Removed by admin')}`, {
      method: 'DELETE'
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Could not remove date.');
    setStatusMessage('Date removed.');
    notifyCatalogueUpdated();
    await loadCourses(selectedCourseId);
  };

  const requestReindex = async () => {
    if (!selectedCourseId) return;
    const response = await request(`/api/admin/courses/${selectedCourseId}/reindex`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: draft.reason || 'Manual re-index request' })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Could not request re-indexing.');
    setStatusMessage('Course marked for re-indexing.');
    await loadCourses(selectedCourseId);
  };

  const handleBulkFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setStatusMessage('');
    setErrorMessage('');
    const rows = await parseBulkFile(file);
    setBulkRows(rows);
    setBulkPreview([]);
    setStatusMessage(`Loaded ${rows.length} rows from ${file.name}.`);
  };

  const downloadBulkTemplate = () => {
    downloadCsv('exec-ed-bulk-upload-template.csv', buildCsvTemplate());
  };

  const handleBulkSourceMappingFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setStatusMessage('');
    setErrorMessage('');
    const rows = normalizeBulkSourceRows(await parseBulkFile(file));
    setBulkSourceRows(rows);
    setBulkSourcePreview([]);
    setStatusMessage(`Loaded ${rows.length} source mapping rows from ${file.name}.`);
  };

  const handleBulkSourceFiles = (event) => {
    const files = Array.from(event.target.files || []);
    setBulkSourceFiles(files);
    setBulkSourcePreview([]);
    if (files.length) {
      setStatusMessage(`${files.length} source file${files.length === 1 ? '' : 's'} selected.`);
    }
  };

  const downloadBulkSourceTemplate = () => {
    downloadCsv('exec-ed-bulk-source-template.csv', buildSourceCsvTemplate());
  };

  const buildBulkSourcePreview = () => {
    const courseByCode = new Map(courses.map((course) => [String(course.programmeCode || '').trim().toLowerCase(), course]));
    const fileByName = new Map(bulkSourceFiles.map((file) => [sourceFileKey(file.name), file]));
    const preview = bulkSourceRows.map((row) => {
      const course = courseByCode.get(row.programmeCode.toLowerCase());
      const matchedFile = row.fileName ? fileByName.get(sourceFileKey(row.fileName)) : null;
      const warnings = [];
      if (!row.programmeCode) warnings.push('Programme code is required.');
      if (row.programmeCode && !course) warnings.push(`Programme code ${row.programmeCode} was not found.`);
      if (!bulkSourceDocumentTypes.includes(row.documentType)) warnings.push('Document type must be case-study, prework, or timetable.');
      if (!row.fileName && !row.sourceUrl) warnings.push('File name or source URL is required.');
      if (row.fileName && !matchedFile) warnings.push(`File ${row.fileName} was not selected.`);
      return {
        ...row,
        matchedTitle: course?.title || '',
        matchedFileName: matchedFile?.name || '',
        warnings,
        ready: warnings.length === 0
      };
    });
    setBulkSourcePreview(preview);
    return preview;
  };

  const applyBulkSourceUpload = async () => {
    const preview = bulkSourcePreview.length && bulkSourcePreview.every((row) => Object.prototype.hasOwnProperty.call(row, 'ready'))
      ? bulkSourcePreview
      : buildBulkSourcePreview();
    if (preview.some((row) => !row.ready)) {
      throw new Error('Resolve source upload warnings before applying.');
    }
    const formData = new FormData();
    formData.append('rows', JSON.stringify(bulkSourceRows));
    bulkSourceFiles.forEach((file) => formData.append('files', file));
    const response = await request('/api/admin/source-documents/bulk', {
      method: 'POST',
      body: formData
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Could not apply bulk source upload.');
    setBulkSourcePreview(Array.isArray(data.results) ? data.results : []);
    setStatusMessage(`Bulk source upload complete: ${data.summary?.created || 0} created, ${data.summary?.failed || 0} failed.`);
    notifyCatalogueUpdated();
  };

  const previewBulk = async () => {
    const response = await request('/api/admin/courses/bulk-preview', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rows: bulkRows })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Could not preview bulk upload.');
    setBulkPreview(Array.isArray(data.preview) ? data.preview : []);
  };

  const applyBulk = async () => {
    const response = await request('/api/admin/courses/bulk-apply', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rows: bulkRows })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Could not apply bulk upload.');
    setBulkPreview(Array.isArray(data.results) ? data.results : []);
    setStatusMessage('Bulk updates applied.');
    notifyCatalogueUpdated();
    await loadCourses(selectedCourseId);
  };

  const backfillBaseCatalogue = async () => {
    const response = await request('/api/admin/courses/base-backfill', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Could not backfill base catalogue.');
    const result = data.result || {};
    setBaseBackfillResult(result);
    setStatusMessage(`Base catalogue backfill complete: ${result.created || 0} created, ${result.updated || 0} updated, ${result.skipped || 0} skipped.`);
    notifyCatalogueUpdated();
    await loadCourses(selectedCourseId);
  };

  const uploadSourceDocument = async () => {
    if (!selectedCourseId) throw new Error('Select a course before uploading a source document.');
    if (!sourceFile && !sourceDraft.sourceUrl) throw new Error('Choose a file or add a source URL.');
    const formData = new FormData();
    formData.append('courseId', String(selectedCourseId));
    formData.append('title', sourceDraft.title || sourceFile?.name || 'Source document');
    formData.append('sourceType', sourceDraft.sourceType || 'web');
    formData.append('classification', sourceDraft.classification || 'approved-programme-source');
    formData.append('status', 'approved');
    formData.append('sourceUrl', sourceDraft.sourceUrl || '');
    formData.append('notes', sourceDraft.notes || '');
    if (sourceFile) formData.append('file', sourceFile);
    const response = await request('/api/admin/source-documents', {
      method: 'POST',
      body: formData
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Could not upload source document.');
    setSourceFile(null);
    setSourceDraft(emptySourceDocument);
    setStatusMessage(data.warning ? `Source uploaded with note: ${data.warning}` : 'Source document uploaded.');
    await loadSourceDocuments(selectedCourseId);
  };

  const uploadFaqSourceDocument = async (courseId, file = faqUploadFile, rowCount = faqUploadPreview.length) => {
    if (!courseId || !file) return null;
    const formData = new FormData();
    formData.append('courseId', courseId);
    formData.append('title', file.name || 'FAQ upload source');
    formData.append('sourceType', file.name?.split('.').pop() || 'faq-upload');
    formData.append('classification', 'faq-upload-source');
    formData.append('status', 'approved');
    formData.append('notes', `Original FAQ upload file retained after applying ${rowCount || 0} FAQ row${rowCount === 1 ? '' : 's'}.`);
    formData.append('file', file);
    const response = await request('/api/admin/source-documents', {
      method: 'POST',
      body: formData
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Could not retain FAQ upload file.');
    return data;
  };

  const downloadSourceDocument = async (document) => {
    if (!document?.id) return;
    const response = await request(`/api/admin/source-documents/${document.id}/download`);
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.message || 'Could not download source file.');
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = window.document.createElement('a');
    link.href = url;
    link.download = document.filename || document.title || `source-document-${document.id}`;
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const extractSourceDocument = async (document) => {
    const response = await request(`/api/admin/source-documents/${document.id}/extract`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Could not extract source document.');
    setExtractionPreview({ document: data.document, extraction: data.extraction });
    setStatusMessage('Extraction ready for review.');
    await loadSourceDocuments(selectedCourseId);
  };

  const applyExtraction = async () => {
    if (!extractionPreview?.document?.id) return;
    const response = await request(`/api/admin/source-documents/${extractionPreview.document.id}/apply`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        courseId: selectedCourseId,
        extractionPayload: extractionPreview.extraction,
        reason: 'Admin reviewed source extraction'
      })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Could not apply extraction.');
    setExtractionPreview(null);
    setStatusMessage('Reviewed extraction applied to course.');
    notifyCatalogueUpdated();
    await loadCourses(selectedCourseId);
    await loadSourceDocuments(selectedCourseId);
  };

  const saveFaq = async () => {
    if (!selectedCourseId && activeAdminTab === 'add') {
      const nextFaq = {
        ...faqDraft,
        id: faqDraft.id || `local-${Date.now()}`,
        sequenceNumber: faqDraft.sequenceNumber || courseFaqs.length + 1
      };
      setCourseFaqs((current) => {
        const existingIndex = current.findIndex((faq) => faq.id === nextFaq.id);
        if (existingIndex >= 0) {
          return current.map((faq) => (faq.id === nextFaq.id ? nextFaq : faq));
        }
        return [...current, nextFaq];
      });
      setFaqDraft(emptyFaq);
      setStatusMessage('FAQ added to this draft course.');
      return;
    }
    if (!selectedCourseId) return;
    const method = faqDraft.id ? 'PUT' : 'POST';
    const url = faqDraft.id
      ? `/api/admin/courses/${selectedCourseId}/faqs/${faqDraft.id}`
      : `/api/admin/courses/${selectedCourseId}/faqs`;
    const response = await request(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(faqDraft)
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Could not save FAQ.');
    setFaqDraft(emptyFaq);
    setStatusMessage('Course FAQ saved.');
    await loadCourseFaqs(selectedCourseId);
  };

  const editFaq = (faq) => {
    setFaqDraft({
      id: faq.id,
      question: faq.question || '',
      answer: faq.answer || '',
      sourceDocument: faq.sourceDocument || '',
      sectionTitle: faq.sectionTitle || '',
      sequenceNumber: faq.sequenceNumber ?? ''
    });
  };

  const deleteFaq = async (faq) => {
    if (!selectedCourseId && activeAdminTab === 'add') {
      setCourseFaqs((current) => current.filter((item) => item.id !== faq.id));
      setStatusMessage('FAQ removed from this draft course.');
      return;
    }
    if (!selectedCourseId || !faq?.id) return;
    const response = await request(`/api/admin/courses/${selectedCourseId}/faqs/${faq.id}`, {
      method: 'DELETE'
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Could not delete FAQ.');
    setStatusMessage('Course FAQ deleted.');
    await loadCourseFaqs(selectedCourseId);
  };

  const handleFaqFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setFaqUploadFile(file);
    const rows = normalizeFaqUploadRows(await parseBulkFile(file));
    setFaqUploadPreview(rows);
    setStatusMessage(`Loaded ${rows.length} FAQ rows from ${file.name}.`);
  };

  const applyFaqUpload = async () => {
    if (!selectedCourseId && activeAdminTab === 'add') {
      if (faqUploadPreview.length === 0) return;
      setCourseFaqs((current) => [
        ...current,
        ...faqUploadPreview
          .filter((row) => row.question && row.answer)
          .map((row, index) => ({
            id: `local-upload-${Date.now()}-${index}`,
            question: row.question,
            answer: row.answer,
            sourceDocument: row.sourceDocument,
            sectionTitle: row.sectionTitle,
            sequenceNumber: row.sequenceNumber || current.length + index + 1
          }))
      ]);
      setFaqUploadPreview([]);
      setStatusMessage('FAQ rows added to this draft course.');
      return;
    }
    if (!selectedCourseId || faqUploadPreview.length === 0) return;
    const appliedRowCount = faqUploadPreview.length;
    const response = await request(`/api/admin/courses/${selectedCourseId}/faqs/bulk`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rows: faqUploadPreview })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Could not apply FAQ upload.');
    const postApplyWarnings = [];
    if (faqUploadFile) {
      try {
        await uploadFaqSourceDocument(selectedCourseId, faqUploadFile, appliedRowCount);
      } catch (error) {
        postApplyWarnings.push(`FAQ source file was not retained: ${error.message}`);
      }
    }
    setFaqUploadPreview([]);
    setFaqUploadFile(null);
    setStatusMessage(`${data.count || 0} FAQs applied to this course.${faqUploadFile ? ' Source file retained.' : ''}`);
    if (postApplyWarnings.length) setErrorMessage(postApplyWarnings.join(' '));
    await loadCourseFaqs(selectedCourseId);
    await loadSourceDocuments(selectedCourseId);
  };

  const runAction = async (action) => {
    try {
      setLoading(true);
      setStatusMessage('');
      setErrorMessage('');
      await action();
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const renderCourseActions = () => (
    <div className="catalogue-admin-actions">
      {!isEditing ? (
        <button type="button" className="icon-btn catalogue-admin-action-icon primary" onClick={startEditing} disabled={!canEditExistingCourse || loading} aria-label="Edit course" title="Edit course">
          <Pencil size={15} />
        </button>
      ) : (
        <>
          {activeAdminTab === 'update' && selectedCourseId && (
            <button type="button" className="icon-btn catalogue-admin-action-icon" onClick={() => runAction(requestReindex)} disabled={loading} aria-label="Re-index course" title="Re-index course">
              <RotateCw size={15} />
            </button>
          )}
          <button type="button" className="icon-btn catalogue-admin-action-icon" onClick={cancelEditing} disabled={loading} aria-label="Cancel changes" title="Cancel changes">
            <X size={15} />
          </button>
          <button type="button" className="icon-btn catalogue-admin-action-icon" onClick={() => runAction(saveDraft)} disabled={loading} aria-label={activeAdminTab === 'add' ? 'Save draft' : 'Save course'} title={activeAdminTab === 'add' ? 'Save draft' : 'Save course'}>
            <Save size={15} />
          </button>
          {activeAdminTab !== 'bulk' && (
            <button type="button" className="icon-btn catalogue-admin-action-icon primary" onClick={() => runAction(publishDraft)} disabled={(activeAdminTab === 'update' && !selectedCourseId) || loading} aria-label="Publish course" title="Publish course">
              <Check size={15} />
            </button>
          )}
        </>
      )}
    </div>
  );

  const renderDateManager = () => (
    <div className="catalogue-admin-record-dates">
      <div className="recommendation-section-header catalogue-admin-subsection-header">
        <div>
          <div className="section-label">Course Dates</div>
          <h3>Start dates</h3>
        </div>
        <CalendarPlus size={18} />
      </div>

      {isEditing && (
        <div className="catalogue-admin-date-form">
          <input type="date" value={dateDraft.startDate} onChange={(event) => setDateDraft((current) => ({ ...current, startDate: event.target.value }))} />
          <select value={dateDraft.status} onChange={(event) => setDateDraft((current) => ({ ...current, status: event.target.value }))}>
            {dateStatuses.map((status) => <option key={status} value={status}>{status}</option>)}
          </select>
          <input type="date" value={dateDraft.applicationDeadline || ''} onChange={(event) => setDateDraft((current) => ({ ...current, applicationDeadline: event.target.value }))} aria-label="Application deadline" />
          <input value={dateDraft.location || ''} onChange={(event) => setDateDraft((current) => ({ ...current, location: event.target.value }))} placeholder="Location override" />
          <input value={dateDraft.format || ''} onChange={(event) => setDateDraft((current) => ({ ...current, format: event.target.value }))} placeholder="Format override" />
          <input type="number" value={dateDraft.feeOverride ?? ''} onChange={(event) => setDateDraft((current) => ({ ...current, feeOverride: event.target.value }))} placeholder="Fee override" />
          <input value={dateDraft.sourceUrl || ''} onChange={(event) => setDateDraft((current) => ({ ...current, sourceUrl: event.target.value }))} placeholder="Source URL" />
          <input value={dateDraft.reason || ''} onChange={(event) => setDateDraft((current) => ({ ...current, reason: event.target.value }))} placeholder="Reason" />
          <button type="button" className="btn-secondary" onClick={() => runAction(saveDate)} disabled={!selectedCourseId || !dateDraft.startDate || loading}>
            {dateDraft.id ? 'Update date' : 'Add date'}
          </button>
          {dateDraft.id && <button type="button" className="btn-secondary" onClick={() => setDateDraft(emptyDate)}><X size={14} />Clear</button>}
        </div>
      )}

      <div className="catalogue-admin-date-list">
        {(selectedCourse?.startDateRecords || []).map((record) => (
          <div key={record.id || record.startDate} className={`catalogue-admin-date-row ${record.status} ${isEditing ? 'has-actions' : ''}`}>
            <strong>{record.startDate}</strong>
            <span>{record.status}</span>
            <span>{record.applicationDeadline ? `Deadline ${record.applicationDeadline}` : 'No deadline'}</span>
            <span>{record.location || selectedCourse.location || 'No location'}</span>
            <span>{record.format || selectedCourse.format || 'No format'}</span>
            <span>{record.feeOverride ? formatFee(record.feeOverride) : 'No fee override'}</span>
            {isEditing && (
              <>
                <button type="button" onClick={() => editDate(record)}>Edit</button>
                <button type="button" onClick={() => runAction(() => cancelDate(record))}>Cancel</button>
                <button type="button" onClick={() => runAction(() => removeDate(record))}><Trash2 size={13} /></button>
              </>
            )}
          </div>
        ))}
        {(!selectedCourse?.startDateRecords || selectedCourse.startDateRecords.length === 0) && <p className="empty-state">No start dates yet.</p>}
      </div>
    </div>
  );

  const renderCourseFaqs = () => {
    if (activeAdminTab === 'bulk' || (activeAdminTab === 'update' && !selectedCourseId)) {
      return null;
    }

    return (
      <section className="catalogue-admin-faqs catalogue-admin-record-section">
        <div className="recommendation-section-header catalogue-admin-subsection-header">
          <div>
            <div className="section-label">Course FAQs</div>
            <h3>Approved Q&A for Query</h3>
          </div>
          <FileText size={18} />
        </div>

        {isEditing && (
          <>
            <div className="catalogue-admin-faq-form">
              <input value={faqDraft.question} onChange={(event) => setFaqDraft((current) => ({ ...current, question: event.target.value }))} placeholder="Question" />
              <textarea rows={2} value={faqDraft.answer} onChange={(event) => setFaqDraft((current) => ({ ...current, answer: event.target.value }))} placeholder="Answer" />
              <input value={faqDraft.sourceDocument} onChange={(event) => setFaqDraft((current) => ({ ...current, sourceDocument: event.target.value }))} placeholder="Source document" />
              <input value={faqDraft.sectionTitle} onChange={(event) => setFaqDraft((current) => ({ ...current, sectionTitle: event.target.value }))} placeholder="Section title" />
              <input type="number" value={faqDraft.sequenceNumber ?? ''} onChange={(event) => setFaqDraft((current) => ({ ...current, sequenceNumber: event.target.value }))} placeholder="Order" />
              <button type="button" className="btn-secondary" onClick={() => runAction(saveFaq)} disabled={!faqDraft.question.trim() || !faqDraft.answer.trim() || loading}>
                {faqDraft.id ? 'Update FAQ' : 'Add FAQ'}
              </button>
              {faqDraft.id && <button type="button" className="btn-secondary" onClick={() => setFaqDraft(emptyFaq)}><X size={14} />Clear</button>}
            </div>

            <div className="catalogue-admin-faq-upload">
              <input type="file" accept=".csv,.tsv,.xlsx,.xls" onChange={(event) => runAction(() => handleFaqFile(event))} />
              <button type="button" className="btn-secondary" onClick={() => runAction(applyFaqUpload)} disabled={faqUploadPreview.length === 0 || loading}>Apply FAQ upload</button>
            </div>

            {faqUploadPreview.length > 0 && (
              <div className="catalogue-admin-faq-preview">
                {faqUploadPreview.slice(0, 6).map((row) => (
                  <div key={`${row.rowNumber}-${row.question}`} className={!row.question || !row.answer ? 'warning' : ''}>
                    <strong>Row {row.rowNumber}: {row.question || 'Missing question'}</strong>
                    <span>{row.answer || 'Missing answer'}</span>
                  </div>
                ))}
                {faqUploadPreview.length > 6 && <p className="empty-state">{faqUploadPreview.length - 6} more FAQ rows loaded.</p>}
              </div>
            )}
          </>
        )}

        <div className="catalogue-admin-faq-list">
          {courseFaqs.map((faq) => (
            <div key={faq.id || faq.question} className="catalogue-admin-faq-row">
              <div>
                <strong>{faq.question}</strong>
                <span>{faq.answer}</span>
                {(faq.sourceDocument || faq.sectionTitle) && <small>{[faq.sourceDocument, faq.sectionTitle].filter(Boolean).join(' - ')}</small>}
              </div>
              {isEditing && (
                <div className="catalogue-admin-faq-row-actions">
                  <button type="button" onClick={() => editFaq(faq)}>Edit</button>
                  <button type="button" onClick={() => runAction(() => deleteFaq(faq))}><Trash2 size={13} /></button>
                </div>
              )}
            </div>
          ))}
          {courseFaqs.length === 0 && <p className="empty-state">No approved FAQs linked to this course yet.</p>}
        </div>
      </section>
    );
  };

  const renderSourceGovernance = () => {
    if (activeAdminTab !== 'update' || !selectedCourseId) {
      return null;
    }

    return (
      <section className="catalogue-admin-sources catalogue-admin-record-section">
        <div className="recommendation-section-header catalogue-admin-subsection-header">
          <div>
            <div className="section-label">Source Governance</div>
            <h3>Documents, timetable, prework, case studies, and traceability</h3>
          </div>
          <FileText size={18} />
        </div>

        {isEditing && (
          <div className="catalogue-admin-source-form">
            <input {...readOnlyFieldProps} value={sourceDraft.title} onChange={(event) => setSourceDraft((current) => ({ ...current, title: event.target.value }))} placeholder="Source title" />
            <select
              {...readOnlyFieldProps}
              value={sourceDraft.sourceType}
              onChange={(event) => {
                const nextType = event.target.value;
                setSourceDraft((current) => ({
                  ...current,
                  sourceType: nextType,
                  classification: sourceTypeClassifications[nextType] || current.classification || 'approved-programme-source'
                }));
              }}
            >
              <option value="web">Web page</option>
              <option value="pdf">PDF</option>
              <option value="docx">Handbook / Word</option>
              <option value="pptx">Faculty deck</option>
              <option value="brochure">Brochure</option>
              <option value="case-study">Case study</option>
              <option value="prework">Prework</option>
              <option value="timetable">Timetable</option>
            </select>
            <input {...readOnlyFieldProps} value={sourceDraft.classification} onChange={(event) => setSourceDraft((current) => ({ ...current, classification: event.target.value }))} placeholder="Classification" />
            <input {...readOnlyFieldProps} value={sourceDraft.sourceUrl} onChange={(event) => setSourceDraft((current) => ({ ...current, sourceUrl: event.target.value }))} placeholder="Source URL" />
            <input {...readOnlyFieldProps} value={sourceDraft.notes} onChange={(event) => setSourceDraft((current) => ({ ...current, notes: event.target.value }))} placeholder="Review note" />
            <input type="file" accept=".pdf,.docx,.pptx,.txt,.md,.html,.htm,.csv" onChange={(event) => setSourceFile(event.target.files?.[0] || null)} disabled={!isEditing} />
            <button type="button" className="btn-secondary" onClick={() => runAction(uploadSourceDocument)} disabled={!isEditing || !selectedCourseId || loading}>
              <Upload size={15} />
              Upload source
            </button>
          </div>
        )}

        <div className="catalogue-admin-source-list">
          {sourceDocuments.map((document) => (
            <div key={document.id} className="catalogue-admin-source-row">
              <div>
                <strong>{document.title}</strong>
                <span>{document.sourceType || 'source'} - {document.classification || 'unclassified'} - {document.extractionStatus || 'not extracted'}</span>
                <small>{document.isDownloadable ? `Original file retained${document.byteSize ? ` - ${Math.round(document.byteSize / 1024)} KB` : ''}` : 'Original file not retained'}</small>
                {document.sourceUrl && <small>{document.sourceUrl}</small>}
              </div>
              <div className="catalogue-admin-source-actions">
                {document.isDownloadable && (
                  <button type="button" onClick={() => runAction(() => downloadSourceDocument(document))}>
                    <Download size={13} />
                    Download
                  </button>
                )}
                {isEditing && <button type="button" onClick={() => runAction(() => extractSourceDocument(document))}>Extract</button>}
              </div>
            </div>
          ))}
          {sourceDocuments.length === 0 && <p className="empty-state">No source documents linked to this course yet.</p>}
        </div>

        {extractionPreview && (
          <div className="catalogue-admin-extraction-review">
            <div className="section-label">Review Extraction</div>
            <pre>{JSON.stringify(cleanExtractedFields(extractionPreview.extraction?.fields || {}), null, 2)}</pre>
            <div className="catalogue-admin-actions">
              <button type="button" className="btn-primary" onClick={() => runAction(applyExtraction)} disabled={!isEditing || loading}>
                <Check size={15} />
                Apply reviewed fields
              </button>
              <button type="button" className="btn-secondary" onClick={() => setExtractionPreview(null)}>
                <X size={14} />
                Discard
              </button>
            </div>
          </div>
        )}

        <div className="catalogue-admin-field-sources">
          <div className="section-label">Field Sources</div>
          {fieldSources.slice(0, 12).map((source) => (
            <div key={source.id} className="catalogue-admin-field-source">
              <strong>{source.fieldPath}</strong>
              <span>{source.sourceTitle || source.sourceUrl || 'Source recorded'}{source.confidence !== null ? ` - confidence ${source.confidence}` : ''}</span>
              {source.evidenceText && <small>{source.evidenceText}</small>}
            </div>
          ))}
          {fieldSources.length === 0 && <p className="empty-state">No field-level traceability recorded yet.</p>}
        </div>
      </section>
    );
  };

  const renderNewCourseBrochure = () => {
    if (activeAdminTab !== 'add') {
      return null;
    }
    const populatedFields = Object.keys(cleanExtractedFields(newCourseSourcePreview?.extraction?.fields || {}));
    const warnings = Array.isArray(newCourseSourcePreview?.extraction?.warnings)
      ? newCourseSourcePreview.extraction.warnings.filter(Boolean)
      : [];

    return (
      <section className="catalogue-admin-brochure-ingest catalogue-admin-record-section">
        <div className="catalogue-admin-brochure-picker">
          <span>Autofill from Brochure</span>
          <input type="file" accept=".pdf,.docx,.pptx,.txt,.md,.html,.htm" onChange={(event) => runAction(() => handleNewCourseBrochureFile(event))} disabled={loading} />
          {loading && <small>Extracting...</small>}
        </div>

        {newCourseSourcePreview && (
          <div className="catalogue-admin-brochure-review">
            <div>
              <strong>{newCourseSourcePreview.document?.title || 'Brochure extraction ready'}</strong>
              <span>{populatedFields.length} fields populated</span>
            </div>
            {warnings.length > 0 && <small>{warnings.join(' ')}</small>}
          </div>
        )}
      </section>
    );
  };

  const renderCoursePicker = () => (
    <section className="card catalogue-admin-course-picker">
      <div className="catalogue-search-field catalogue-admin-course-search">
        <label className="input-label" htmlFor="catalogue-admin-course-search">Search</label>
        <div className="catalog-search-wrap">
          <Search size={16} className="catalog-search-icon" />
          <input
            id="catalogue-admin-course-search"
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search by title, code, faculty, or topic"
            className="catalog-search-input"
            autoComplete="off"
          />
        </div>
      </div>
      {hasCourseSearchQuery && (
        <div className="catalogue-admin-course-list schema-scroll">
          {filteredCourses.slice(0, 8).map((course) => (
            <button
              key={course.id}
              type="button"
              className={course.id === selectedCourseId ? 'active' : ''}
              onClick={() => selectCourseForUpdate(course.id)}
            >
              <span>{course.programmeCode ? `${course.programmeCode}: ` : ''}{course.title}</span>
              <small>{course.status || 'published'} - {course.discipline || 'No discipline'}</small>
            </button>
          ))}
          {filteredCourses.length === 0 && <p className="empty-state">No matching courses.</p>}
        </div>
      )}
    </section>
  );

  return (
    <section className={embedded ? 'page-catalogue-admin catalogue-admin-embedded' : 'page page-catalogue-admin'}>
      <div className="catalogue-admin-shell">
        <aside className="card settings-agent-list catalogue-admin-section-list" role="tablist" aria-label="Catalogue admin sections">
          <button type="button" className={`settings-agent-tab ${activeAdminTab === 'update' ? 'active' : ''}`} onClick={openUpdateTab}>
            <span>Update Course</span>
            <small>Edit existing programme records, dates, and source governance.</small>
          </button>
          <button type="button" className={`settings-agent-tab ${activeAdminTab === 'add' ? 'active' : ''}`} onClick={createNewDraft}>
            <span>Add Course</span>
            <small>Create a draft programme before publishing it to the catalogue.</small>
          </button>
          <button type="button" className={`settings-agent-tab ${activeAdminTab === 'bulk' ? 'active' : ''}`} onClick={openBulkTab}>
            <span>Bulk Upload</span>
            <small>Add new draft programmes or update many existing programmes at once.</small>
          </button>
        </aside>

        <div className="catalogue-admin-main">
          {(statusMessage || errorMessage) && (
            <div className={`catalogue-admin-status ${errorMessage ? 'error' : 'success'}`}>
              {errorMessage || statusMessage}
            </div>
          )}

          <div className="catalogue-admin-layout catalogue-admin-layout-single">
        <div className="catalogue-admin-workspace">
          {activeAdminTab === 'update' && renderCoursePicker()}
          {activeAdminTab !== 'bulk' && (
            <>
            <section className={`card catalogue-admin-editor ${isEditing ? 'is-editing' : 'is-viewing'}`}>
            <div className="recommendation-section-header">
              <div>
                {activeAdminTab !== 'add' && <div className="section-label">Course Record</div>}
                <div className="catalogue-admin-title-row">
                  <h2>{activeAdminTab === 'add' ? 'Add Course' : (draft.title || 'Course record')}</h2>
                  {activeAdminTab !== 'add' && isEditing && (
                    <span className={`catalogue-admin-mode-chip ${isEditing ? 'editing' : ''}`}>
                      Edit mode
                    </span>
                  )}
                </div>
              </div>
              {renderCourseActions()}
            </div>

            {isEditing && activeAdminTab !== 'add' && validationErrors.length > 0 && (
              <div className="catalogue-admin-validation">
                {validationErrors.map((error) => <span key={error}>{error}</span>)}
              </div>
            )}

            {renderNewCourseBrochure()}

            <div className="catalogue-admin-form-grid">
              <label><RequiredFieldLabel field="title">Title</RequiredFieldLabel><input {...readOnlyFieldProps} value={draft.title} onChange={(event) => updateDraft('title', event.target.value)} /></label>
              <label><RequiredFieldLabel field="programmeCode">Programme code</RequiredFieldLabel><input {...readOnlyFieldProps} value={draft.programmeCode || ''} onChange={(event) => updateDraft('programmeCode', event.target.value)} /></label>
              <label><RequiredFieldLabel field="discipline">Discipline</RequiredFieldLabel><input {...readOnlyFieldProps} value={draft.discipline || ''} onChange={(event) => updateDraft('discipline', event.target.value)} /></label>
              <label>Programme pillar/category<input {...readOnlyFieldProps} value={draft.cimCategory || ''} onChange={(event) => updateDraft('cimCategory', event.target.value)} /></label>
              <label><RequiredFieldLabel field="duration">Duration</RequiredFieldLabel><input {...readOnlyFieldProps} value={draft.duration || ''} onChange={(event) => updateDraft('duration', event.target.value)} /></label>
              <label>Fee<input {...readOnlyFieldProps} type="number" value={draft.fees ?? ''} onChange={(event) => updateDraft('fees', event.target.value)} /></label>
              <label><RequiredFieldLabel field="format">Format</RequiredFieldLabel><input {...readOnlyFieldProps} value={draft.format || ''} onChange={(event) => updateDraft('format', event.target.value)} /></label>
              <label>Location<input {...readOnlyFieldProps} value={draft.location || ''} onChange={(event) => updateDraft('location', event.target.value)} /></label>
              <label>Faculty/instructor<input {...readOnlyFieldProps} value={draft.instructor || ''} onChange={(event) => updateDraft('instructor', event.target.value)} /></label>
              <label>Status<select {...readOnlyFieldProps} value={draft.status} onChange={(event) => updateDraft('status', event.target.value)}>{courseStatuses.map((status) => <option key={status} value={status}>{status}</option>)}</select></label>
              <label className="catalogue-admin-full"><RequiredFieldLabel field="sourceUrl">Source URL</RequiredFieldLabel><input {...readOnlyFieldProps} value={draft.sourceUrl || ''} onChange={(event) => updateDraft('sourceUrl', event.target.value)} /></label>
              <label>Source classification<input {...readOnlyFieldProps} value={draft.sourceClassification || ''} onChange={(event) => updateDraft('sourceClassification', event.target.value)} /></label>
              <label>Source notes<input {...readOnlyFieldProps} value={draft.sourceNotes || ''} onChange={(event) => updateDraft('sourceNotes', event.target.value)} /></label>
              <label className="catalogue-admin-full"><RequiredFieldLabel field="description">Description</RequiredFieldLabel><textarea {...readOnlyFieldProps} rows={3} value={draft.description || ''} onChange={(event) => updateDraft('description', event.target.value)} /></label>
              <label className="catalogue-admin-full">Target audience<textarea {...readOnlyFieldProps} rows={2} value={draft.targetAudience || ''} onChange={(event) => updateDraft('targetAudience', event.target.value)} /></label>
              <label className="catalogue-admin-full">Objectives<textarea {...readOnlyFieldProps} rows={2} value={draft.objectives || ''} onChange={(event) => updateDraft('objectives', event.target.value)} /></label>
              <label className="catalogue-admin-full">Key topics<textarea {...readOnlyFieldProps} rows={3} value={draft.keyTopics || ''} onChange={(event) => updateDraft('keyTopics', event.target.value)} placeholder="One topic per line" /></label>
              <label className="catalogue-admin-full">Outcomes<textarea {...readOnlyFieldProps} rows={2} value={draft.outcomes || ''} onChange={(event) => updateDraft('outcomes', event.target.value)} /></label>
              <label className="catalogue-admin-full">Testimonials<textarea {...readOnlyFieldProps} rows={3} value={draft.testimonials || ''} onChange={(event) => updateDraft('testimonials', event.target.value)} placeholder="One testimonial per line" /></label>
              <label>Average age<input {...readOnlyFieldProps} value={draft.averageAge || ''} onChange={(event) => updateDraft('averageAge', event.target.value)} /></label>
              <label>Average work experience<input {...readOnlyFieldProps} value={draft.averageExperience || ''} onChange={(event) => updateDraft('averageExperience', event.target.value)} /></label>
              <label>Academic director<input {...readOnlyFieldProps} value={draft.academicDirector || ''} onChange={(event) => updateDraft('academicDirector', event.target.value)} /></label>
              <label>Programme director<input {...readOnlyFieldProps} value={draft.programmeDirector || ''} onChange={(event) => updateDraft('programmeDirector', event.target.value)} /></label>
              <label className="catalogue-admin-full">Top industries represented<textarea {...readOnlyFieldProps} rows={3} value={draft.topIndustries || ''} onChange={(event) => updateDraft('topIndustries', event.target.value)} placeholder="One industry per line" /></label>
              <label className="catalogue-admin-full">Top nationalities<textarea {...readOnlyFieldProps} rows={2} value={draft.topNationalities || ''} onChange={(event) => updateDraft('topNationalities', event.target.value)} placeholder="One nationality per line" /></label>
              <label className="catalogue-admin-full">Participant seniorities<textarea {...readOnlyFieldProps} rows={2} value={draft.participantSeniorities || ''} onChange={(event) => updateDraft('participantSeniorities', event.target.value)} placeholder="One seniority per line" /></label>
              <label className="catalogue-admin-full">Key details website<input {...readOnlyFieldProps} value={draft.keyDetailsWebsite || ''} onChange={(event) => updateDraft('keyDetailsWebsite', event.target.value)} /></label>
              <label className="catalogue-admin-full">Key details source note<textarea {...readOnlyFieldProps} rows={2} value={draft.keyDetailsSourceNote || ''} onChange={(event) => updateDraft('keyDetailsSourceNote', event.target.value)} /></label>
              <label className="catalogue-admin-full">Participant profile<textarea {...readOnlyFieldProps} rows={2} value={draft.participantProfile || ''} onChange={(event) => updateDraft('participantProfile', event.target.value)} /></label>
              <label className="catalogue-admin-full">Timetable<textarea {...readOnlyFieldProps} rows={2} value={draft.timetable || ''} onChange={(event) => updateDraft('timetable', event.target.value)} /></label>
              <label className="catalogue-admin-full">Session overview<textarea {...readOnlyFieldProps} rows={2} value={draft.sessionOverview || ''} onChange={(event) => updateDraft('sessionOverview', event.target.value)} /></label>
              <label className="catalogue-admin-full">Faculty content summary<textarea {...readOnlyFieldProps} rows={2} value={draft.facultyContentSummary || ''} onChange={(event) => updateDraft('facultyContentSummary', event.target.value)} /></label>
              <label className="catalogue-admin-full">Intended impact<textarea {...readOnlyFieldProps} rows={2} value={draft.intendedImpact || ''} onChange={(event) => updateDraft('intendedImpact', event.target.value)} /></label>
              <label className="catalogue-admin-full">Prerequisites<textarea {...readOnlyFieldProps} rows={2} value={draft.prerequisites || ''} onChange={(event) => updateDraft('prerequisites', event.target.value)} /></label>
              <label className="catalogue-admin-full">Sector and industry focus<textarea {...readOnlyFieldProps} rows={2} value={draft.sectorFocus || ''} onChange={(event) => updateDraft('sectorFocus', event.target.value)} /></label>
              <label className="catalogue-admin-full">Organisational outcomes<textarea {...readOnlyFieldProps} rows={2} value={draft.organisationalOutcomes || ''} onChange={(event) => updateDraft('organisationalOutcomes', event.target.value)} /></label>
              <label className="catalogue-admin-full">Source URL or reason for this change<input {...readOnlyFieldProps} value={draft.reason || ''} onChange={(event) => updateDraft('reason', event.target.value)} /></label>
              <label className="catalogue-admin-checkbox">
                <input type="checkbox" checked={draft.certificationEligible} onChange={(event) => updateDraft('certificationEligible', event.target.checked)} disabled={!isEditing} />
                Counts toward CIM/CIF certificate
              </label>
            </div>
            {renderDateManager()}
            {renderCourseFaqs()}
            {renderSourceGovernance()}
            <div className="catalogue-admin-form-actions">
              {renderCourseActions()}
            </div>
          </section>

            {isEditing && activeAdminTab === 'update' && <CoursePreview draft={draft} />}
            </>
          )}

          {activeAdminTab === 'bulk' && (
            <section className="card catalogue-admin-bulk">
            <div className="recommendation-section-header">
              <div>
                <div className="section-label">Bulk Upload</div>
                <h2>Add or update multiple programmes</h2>
              </div>
              <FileSpreadsheet size={18} />
            </div>
            <p className="assistant-info">Use CSV or Excel to create new draft programmes, update existing programme records, and manage dates, fees, source URLs, and change reasons in one reviewed upload.</p>
            <div className="catalogue-admin-bulk-actions">
              <button type="button" className="btn-secondary" onClick={downloadBulkTemplate}>
                <Download size={15} />
                Download CSV template
              </button>
              <input type="file" accept=".csv,.tsv,.xlsx,.xls" onChange={(event) => runAction(() => handleBulkFile(event))} />
              <button type="button" className="btn-secondary" onClick={() => runAction(previewBulk)} disabled={bulkRows.length === 0 || loading}>Preview changes</button>
              <button type="button" className="btn-primary" onClick={() => runAction(applyBulk)} disabled={bulkPreview.length === 0 || loading}>Apply reviewed changes</button>
              <button type="button" className="btn-secondary" onClick={() => runAction(backfillBaseCatalogue)} disabled={loading}>
                <RotateCw size={15} />
                Backfill missing base catalogue
              </button>
            </div>
            {baseBackfillResult && (
              <div className="catalogue-admin-bulk-row">
                <strong>Base catalogue backfill</strong>
                <span>{baseBackfillResult.created || 0} created</span>
                <span>{baseBackfillResult.updated || 0} updated</span>
                <span>{baseBackfillResult.skipped || 0} skipped</span>
              </div>
            )}
            <div className="catalogue-admin-bulk-preview schema-scroll">
              {bulkPreview.map((item) => (
                <div key={item.rowNumber} className={`catalogue-admin-bulk-row ${item.applied === false ? 'error' : ''}`}>
                  <strong>Row {item.rowNumber}: {item.matchedTitle || item.normalized.title || 'New course'}</strong>
                  {item.warnings?.map((warning) => <span key={warning} className="warning">{warning}</span>)}
                  {item.error && <span className="warning">{item.error}</span>}
                  {(item.changes || []).map((change, index) => (
                    <span key={`${change.type}-${change.field}-${index}`}>
                      {change.type}: {change.field} from {String(change.previousValue ?? 'blank')} to {String(change.newValue ?? 'blank')}
                    </span>
                  ))}
                  {item.applied && <span className="success">Applied</span>}
                </div>
              ))}
              {bulkRows.length > 0 && bulkPreview.length === 0 && <p className="empty-state">{bulkRows.length} rows loaded. Preview before applying.</p>}
            </div>
            <div className="catalogue-admin-bulk-source">
              <div className="recommendation-section-header catalogue-admin-subsection-header">
                <div>
                  <div className="section-label">Bulk Source Upload</div>
                  <h3>Attach timetable, prework, and case study files</h3>
                </div>
                <FileText size={18} />
              </div>
              <p className="assistant-info">Upload a mapping file, then select the matching source files. Each row attaches one approved source document to an existing programme code.</p>
              <div className="catalogue-admin-bulk-actions">
                <button type="button" className="btn-secondary" onClick={downloadBulkSourceTemplate}>
                  <Download size={15} />
                  Download source template
                </button>
                <label className="btn-secondary catalogue-admin-file-button">
                  Choose map file
                  <input type="file" accept=".csv,.tsv,.xlsx,.xls" onChange={(event) => runAction(() => handleBulkSourceMappingFile(event))} />
                </label>
                <label className="btn-secondary catalogue-admin-file-button">
                  Choose Multiple Files
                  <input type="file" multiple accept=".pdf,.docx,.pptx,.txt,.md,.html,.htm,.csv" onChange={handleBulkSourceFiles} />
                </label>
                <button type="button" className="btn-secondary" onClick={buildBulkSourcePreview} disabled={bulkSourceRows.length === 0 || loading}>Preview source upload</button>
                <button type="button" className="btn-primary" onClick={() => runAction(applyBulkSourceUpload)} disabled={bulkSourceRows.length === 0 || loading}>Apply source upload</button>
              </div>
              <div className="catalogue-admin-bulk-preview schema-scroll">
                {bulkSourcePreview.map((item) => (
                  <div key={item.rowNumber} className={`catalogue-admin-bulk-row ${item.error || item.applied === false || item.ready === false ? 'error' : ''}`}>
                    <strong>Row {item.rowNumber}: {item.programmeCode || 'No code'} - {item.title || item.fileName || item.documentType || 'Source document'}</strong>
                    <span>{item.matchedTitle || item.courseTitle || 'Course not matched'}{item.documentType ? ` - ${item.documentType}` : ''}</span>
                    {(item.fileName || item.matchedFileName || item.filename) && <span>File: {item.matchedFileName || item.fileName || item.filename}</span>}
                    {item.documentId && <span>Source document ID: {item.documentId}</span>}
                    {(item.warnings || []).map((warning) => <span key={warning} className="warning">{warning}</span>)}
                    {item.error && <span className="warning">{item.error}</span>}
                    {(item.ready || item.applied) && !item.error && <span className="success">{item.applied ? 'Applied' : 'Ready'}</span>}
                  </div>
                ))}
                {bulkSourceRows.length > 0 && bulkSourcePreview.length === 0 && <p className="empty-state">{bulkSourceRows.length} source rows loaded. Preview before applying.</p>}
              </div>
            </div>
          </section>
          )}
        </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default CatalogueAdminPage;
